import PropTypes from 'prop-types';
import Avatar from '@material-ui/core/Avatar';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import TitledPanel from '../../core/TitledPanel';
import Grid from '@material-ui/core/Grid';
import React from 'react';
import GS from '../../grafsync/grafsync';
import { makeCancelable } from '../../core/utils/utils';
import Loading from '../../core/Loading';
import Warning from '../../core/Warning';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { globalStyles } from '../../core/style/gstheme';
import { LANGS } from '../../core/utils/constants';

const styles = () => {
    return Object.assign({
        tableCell: {
            overflow: 'hidden'
        }
    }, globalStyles);
};

const countTypeMapping = {
    PREFLABEL: { "en": "Preferred Labels" },
    ALTLABEL: { "en": "Alternative Labels" }
};

class SchemeStatistics extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            isErrored: false,
            isFailed: false
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.getData();
    }

    onExitWarning = () => this.setState({ isErrored: false });

    async getData() {
        try {
            let fullStats = [];
            const schemeData = await this.loadData("statistics").promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            fullStats = [...schemeData.graph];
            if (this.props.workflow) {
                const workflowData = await this.loadData("workflowstatistics").promise;
                if (this.cancellable && this.cancellable.isCancelled()) return;
                fullStats = fullStats.concat(workflowData.graph);
            }
            this.setState({ data: fullStats, isLoading: false });
        }
        catch (e) {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({
                isErrored: true,
                isLoading: false,
                isFailed: true,
                message: "Could not load all of the data for the statistics view."
            });
        }

    }

    loadData(exportName) {
        this.cancellable = makeCancelable(GS.export(exportName, this.props.id, this.props.graph));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    render() {

        const { classes, workflow } = this.props;
        const { isLoading, isErrored, message, data, isFailed } = this.state;

        if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;
        if (isLoading) return <Loading minHeight="140px" offset="100px" />;
        if (isFailed) return null;

        return (
            <ProjectConsumer>
                {({ defaultLang }) => (
                    <Grid container spacing={2} classes={
                        {
                            "spacing-xs-2": this.props.classes.fullGrid
                        }
                    }>
                        <Grid item xs={12} md={4}>
                            <TitledPanel fontSize="large" title="Basic Statistics">
                                <Typography className={classes.subSubHeading}>Concept Count</Typography>
                                <Typography data-test="concept-count" className={classes.statistic}>{data.find(stat => stat.viewCountType === "CONCEPTCOUNT").viewCount}</Typography>
                                <Typography className={classes.subSubHeading}>Top Concept Count</Typography>
                                <Typography data-test="top-concept-count" className={classes.statistic}>{data.find(stat => stat.viewCountType === "TOPCONCEPTCOUNT").viewCount}</Typography>
                                <Typography className={classes.subSubHeading}>XL Label Count</Typography>
                                <Typography data-test="xllabel-count" className={classes.statistic}>{data.find(stat => stat.viewCountType === "XLLABELCOUNT").viewCount}</Typography>
                            </TitledPanel>
                        </Grid>
                        {Object.keys(countTypeMapping).map((countType, c) =>
                            <Grid item xs={12} md={4} key={"stat-" + c}>
                                <TitledPanel height="full" fontSize="large" title={countTypeMapping[countType].en}>
                                    <Table data-test={countTypeMapping[countType].en}>
                                        <TableBody>
                                            {
                                                data.filter(stat => stat.type === "LabelCount" && stat.viewCountType === countType).sort((a, b) => {
                                                    return a.viewLanguage < b.viewLanguage ? -1 : 1;
                                                })
                                                    .map((stat, i) =>
                                                        <TableRow key={"stat-" + i}>
                                                            <TableCell className={classes.tableCell}><Avatar style={stat.viewLanguage.length < 3 ?
                                                                { height: '40px', width: '40px', fontSize: '16px' }
                                                                :
                                                                { height: '40px', width: '40px', fontSize: '12px' }
                                                            } className={classes.fieldAvatar}>{stat.viewLanguage}</Avatar></TableCell>
                                                            <TableCell className={classes.tableCell} style={{ verticalAlign: 'middle' }}>{LANGS[stat.viewLanguage] ? LANGS[stat.viewLanguage][defaultLang] : ''}</TableCell>
                                                            <TableCell data-test="view-count" align="right" className={classes.statistic}>{stat.viewCount}</TableCell>
                                                        </TableRow>
                                                    )
                                            }
                                        </TableBody>
                                    </Table>
                                </TitledPanel>
                            </Grid>
                        )}
                        {workflow &&
                            <Grid item xs={12} md={4}>
                                <TitledPanel dataTest="workflow-stats" fontSize="large" title="Workflow Statistics">
                                    <Typography paragraph variant="caption">Deprecated resources are not included.</Typography>
                                    <Typography className={classes.subSubHeading}>Uncategorised Concept Count</Typography>
                                    <Typography data-test="uncategorised-concept-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "UNCATEGORISEDCONCEPTCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Draft Concept Count</Typography>
                                    <Typography data-test="draft-concept-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "DRAFTCONCEPTCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Approved Concept Count</Typography>
                                    <Typography data-test="approved-concept-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "APPROVEDCONCEPTCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Rejected Concept Count</Typography>
                                    <Typography data-test="rejected-concept-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "REJECTEDCONCEPTCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Uncategorised XL Label Count</Typography>
                                    <Typography data-test="uncategorised-xllabel-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "UNCATEGORISEDLABELCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Draft XL Label Count</Typography>
                                    <Typography data-test="draft-xllabel-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "DRAFTLABELCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Approved XL Label Count</Typography>
                                    <Typography data-test="approved-xllabel-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "APPROVEDLABELCOUNT").viewCount}
                                    </Typography>
                                    <Typography className={classes.subSubHeading}>Rejected XL Label Count</Typography>
                                    <Typography data-test="rejected-xllabel-count" className={classes.statistic}>
                                        {data.find(stat => stat.viewCountType === "REJECTEDLABELCOUNT").viewCount}
                                    </Typography>
                                </TitledPanel>
                            </Grid>
                        }
                    </Grid>
                )}
            </ProjectConsumer>

        );
    }
}

SchemeStatistics.defaultProps = {
    workflow: false
};

SchemeStatistics.propTypes = {
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    workflow: PropTypes.bool,
    classes: PropTypes.object
};

export default withStyles(styles)(SchemeStatistics);